import React, { createContext, ReactElement, ReactNode, useContext, useState } from 'react';
import {
  useAddKanbanItemsToCartAction,
  useClearAddKanbanItemsToCartAction,
  useClearVerifyKanbanItemsAction,
  useVerifyKanbanItemsAction,
} from 'b2b-apps/core/kanban';
import { KanbanOrderFormValuesType } from 'b2b-apps/core/kanban/components/KanbanOrder';
import { KANBAN_ORDER_MODAL_TYPE } from 'b2b-apps/core/kanban/components/KanbanOrder/KanbanOrderForm/KanbanModal';
import { FormValuesType } from 'b2b-apps/core/kanban/components/LockerSection/LockerSectionForm';
import { useCartStatusData, useRemoveCartAction } from 'b2b-apps/shop/cart/hooks';

interface KanbanContextValue {
  closeModal: () => void;
  enableEditMode: () => void;
  handleAddToCart: () => void;
  handleClearCartAndAddToCart: () => void;
  handleConfirmAddToCart: () => void;
  handleFormClear: (formikResetFormCallback: () => void) => () => void;
  onVerify: (values: KanbanOrderFormValuesType) => void;
  hasOrderChanges: boolean;
  lockerSectionEditMode: boolean;
  modalType: KANBAN_ORDER_MODAL_TYPE | null;
  openClearFormModal: () => void;
  setOrderChanged: () => void;
  shouldConfirmNavigation: boolean;
  handleLockerChange: (values: FormValuesType) => void;
  lockerId?: string;
  lockerDebitorId?: string;
  costCenterManuallyTypedId?: string;
}

interface KanbanContextProviderProps {
  children: ReactNode;
}

export const KanbanContext = createContext<KanbanContextValue | undefined>(undefined);

const mapValues = (values: KanbanOrderFormValuesType) => ({
  items: values.items.filter(v => !!v.sku).map(v => ({
    quantity: v.quantity,
    reference: v.reference,
    sku: v.sku,
  })),
});

export const KanbanContextProvider = ({ children }: KanbanContextProviderProps): ReactElement => {
  const cartStatusData = useCartStatusData();

  const verifyKanbanItems = useVerifyKanbanItemsAction();
  const addKanbanItemsToCart = useAddKanbanItemsToCartAction();
  const clearVerifyKanbanItems = useClearVerifyKanbanItemsAction();
  const clearAddKanbanItemsToCart = useClearAddKanbanItemsToCartAction();
  const removeCart = useRemoveCartAction();

  const [lockerId, setLockerId] = useState<string | undefined>();
  const [lockerDebitorId, setLockerDebitorId] = useState<string | undefined>();
  const [costCenterManuallyTypedId, setCostCenterManuallyTypedId] = useState<string | undefined>();

  const [shouldConfirmNavigation, setShouldConfirmNavigation] = useState(true);
  const [lockerSectionEditMode, setLockerSectionEditMode] = useState(true);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [modalType, setModalType] = useState<KANBAN_ORDER_MODAL_TYPE | null>(null);

  const handleVerifyItems = (values: KanbanOrderFormValuesType): void => {
    setModalType(KANBAN_ORDER_MODAL_TYPE.verify);
    verifyKanbanItems(mapValues(values));
    setHasOrderChanges(false);
  };

  const handleFormClear = (formikResetFormCallback: () => void) => () => {
    formikResetFormCallback();
    setHasOrderChanges(false);
    clearAddKanbanItemsToCart();
    clearVerifyKanbanItems();
    setModalType(null);
  };

  const handleAddToCart = () => {
    if (cartStatusData?.count) {
      setModalType(KANBAN_ORDER_MODAL_TYPE.addToCartConfirm);
    } else {
      setModalType(KANBAN_ORDER_MODAL_TYPE.addToCart);
      addKanbanItemsToCart({ lockerId, costCenterManuallyTypedId });
      setShouldConfirmNavigation(false);
    }
  };

  const handleLockerChange = (values: FormValuesType) => {
    setLockerId(values.lockerId);
    setLockerDebitorId(values.lockerDebitorId);
    setCostCenterManuallyTypedId(values.costCenterManuallyTypedId);
    setLockerSectionEditMode(false);
  };

  const handleConfirmAddToCart = async () => {
    setModalType(KANBAN_ORDER_MODAL_TYPE.addToCart);
    addKanbanItemsToCart({ lockerId, costCenterManuallyTypedId });
    setShouldConfirmNavigation(false);
  };

  const handleClearCartAndAddToCart = async () => {
    setModalType(KANBAN_ORDER_MODAL_TYPE.addToCart);
    removeCart();
    addKanbanItemsToCart({ lockerId, costCenterManuallyTypedId });
    setShouldConfirmNavigation(false);
  };

  return (
    <KanbanContext.Provider value={{
      onVerify: handleVerifyItems,
      closeModal: () => setModalType(null),
      costCenterManuallyTypedId,
      enableEditMode: () => setLockerSectionEditMode(true),
      handleAddToCart,
      handleClearCartAndAddToCart,
      handleConfirmAddToCart,
      handleFormClear,
      handleLockerChange,
      hasOrderChanges,
      lockerId,
      lockerDebitorId,
      lockerSectionEditMode,
      modalType,
      openClearFormModal: () => setModalType(KANBAN_ORDER_MODAL_TYPE.clearForm),
      setOrderChanged: () => setHasOrderChanges(true),
      shouldConfirmNavigation,
    }}>
      {children}
    </KanbanContext.Provider>
  );
};

export const useKanbanContext = (): KanbanContextValue => {
  const context = useContext(KanbanContext);

  if (context === undefined) {
    throw new Error('context is undefined!');
  }

  return context;
};
